/**
 * The ledger's person pickers (the quick add, an item's edit form): search
 * the people already known, and offer to add the one typed when no source
 * has met them yet. Adding resolves like a source would (POST /api/people),
 * so a name or address already known is that person, never a second one.
 */

import { computed, ref, watch } from 'vue'
import { api, unwrap } from './api'
import { newPersonFrom } from './people'

export interface PersonHit {
  id: string
  displayName: string
  email: string | null
}

/** `exclude`: people already picked, left out of the hits. */
export function usePersonPicker(exclude: () => string[] = () => []) {
  const query = ref('')
  const hits = ref<PersonHit[]>([])
  const adding = ref(false)
  const error = ref<string | null>(null)
  let seq = 0

  watch(query, async (q) => {
    const mine = ++seq
    error.value = null
    if (q.trim().length < 2) {
      hits.value = []
      return
    }
    try {
      const found = await unwrap(api.api.people.$get({ query: { q: q.trim(), limit: '8' } }))
      if (mine === seq) hits.value = found.filter((h) => !exclude().includes(h.id))
    } catch {
      if (mine === seq) hits.value = []
    }
  })

  /** The one typed, unless a hit already is them by that name or address. */
  const candidate = computed(() => {
    const p = newPersonFrom(query.value)
    if (!p) return null
    const same = hits.value.some(
      (h) =>
        (p.email && h.email?.toLowerCase() === p.email) ||
        (!p.email && h.displayName.toLowerCase() === p.name?.toLowerCase())
    )
    return same ? null : p
  })

  function clear() {
    seq++
    query.value = ''
    hits.value = []
  }

  /** Add the candidate; the person it resolved to, or null when that failed. */
  async function addCandidate(): Promise<PersonHit | null> {
    const p = candidate.value
    if (!p || adding.value) return null
    adding.value = true
    error.value = null
    try {
      const person = await unwrap(api.api.people.$post({ json: p }))
      clear()
      return { id: person.id, displayName: person.displayName, email: person.email }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Could not add them'
      return null
    } finally {
      adding.value = false
    }
  }

  return { query, hits, candidate, adding, error, clear, addCandidate }
}
